// The store follow toggle's decision logic, free of React and the router so the race-sensitive paths are
// unit-tested against a real QueryClient. `useStoreFollowToggle` wires it to the mutation, navigation and toasts.
import type { QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { qk } from "@/api/queries/qk";
import { storeQuery, type FollowStoreVariables } from "@/api/queries/stores";
import { decideViewerToggle, resolveViewerSession } from "@/api/queries/viewer-session";
import type { Store } from "@/api/types";
import { knownStore } from "./store-follow";

export interface StoreFollowToggleDeps {
  queryClient: QueryClient;
  /** The store as rendered on the button that was pressed: its `isFollowing` is what the viewer saw. */
  store: Store;
  /** Set while a toggle is resolving or in flight; further clicks are ignored. */
  busy: { current: boolean };
  isPending: () => boolean;
  follow: (variables: FollowStoreVariables, callbacks: { onError: (error: unknown) => void; onSettled: () => void }) => void;
  join: () => void;
  claim: () => void;
  toastError: (message: string) => void;
}

/** Stores with a toggle resolving or in flight, per QueryClient: two buttons for one store share the guard. */
const inFlight = new WeakMap<QueryClient, Set<string>>();

function inFlightSlugs(queryClient: QueryClient): Set<string> {
  let slugs = inFlight.get(queryClient);
  if (!slugs) inFlight.set(queryClient, (slugs = new Set()));
  return slugs;
}

function withKnownFollowState(item: unknown, known: Store): unknown {
  if (!item || typeof item !== "object") return item;
  const record = item as Partial<Store> & { store?: Store };
  if (record.slug === known.slug && typeof record.isFollowing === "boolean") {
    return record.isFollowing === known.isFollowing && record.followers === known.followers
      ? item
      : { ...record, isFollowing: known.isFollowing, followers: known.followers };
  }
  if (record.store?.slug === known.slug) {
    return { ...record, store: { ...record.store, isFollowing: known.isFollowing, followers: known.followers } };
  }
  return item;
}

/** Shows the known follow state for one store in every cached copy that carries it: detail, lists, product pages. */
function writeKnownStore(queryClient: QueryClient, known: Store) {
  queryClient.setQueriesData({ queryKey: qk.stores.all() }, (previous: unknown) => {
    if (previous === undefined) return previous;
    if (Array.isArray(previous)) return previous.map((item) => withKnownFollowState(item, known));
    const paged = previous as { items?: unknown[] };
    if (Array.isArray(paged.items)) {
      return { ...paged, items: paged.items.map((item) => withKnownFollowState(item, known)) };
    }
    return withKnownFollowState(previous, known);
  });
  queryClient.setQueriesData({ queryKey: ["products", "detail"] }, (previous: unknown) =>
    previous === undefined || Array.isArray(previous) ? previous : withKnownFollowState(previous, known),
  );
}

/**
 * Store follow is a signed-in feature (M-D9): anonymous visitors join first and come back here, unclaimed accounts
 * claim an identity, and signed-in people toggle on the server.
 *
 * The decision rule: the action is ALWAYS the opposite of what the pressed button showed (`next = !rendered`), so
 * no disagreement between cached copies can invert what the viewer asked for. The best known state (the detail,
 * else a list that isn't waiting on a refetch, else the server) only decides whether a write is needed: if it
 * already equals `next`, the caches are brought up to date and nothing is sent. Writes are idempotent on the server.
 */
export async function toggleStoreFollow(deps: StoreFollowToggleDeps): Promise<void> {
  const { queryClient, store, busy } = deps;
  const slugs = inFlightSlugs(queryClient);
  if (busy.current || deps.isPending() || slugs.has(store.slug)) return;
  busy.current = true;
  slugs.add(store.slug);
  let settlesLater = false;
  const release = () => {
    busy.current = false;
    slugs.delete(store.slug);
  };
  try {
    const session = await resolveViewerSession(queryClient);
    const decision = decideViewerToggle(session, store.isFollowing);
    switch (decision.kind) {
      case "session-error":
        deps.toastError("Couldn't check your sign-in — try again");
        return;
      case "join":
        deps.join();
        return;
      case "claim":
        deps.claim();
        return;
    }
    const next = decision.next;

    let known = knownStore(queryClient, store.slug);
    if (!known) {
      try {
        known = await queryClient.fetchQuery(storeQuery(store.slug));
      } catch {
        deps.toastError("Couldn't load this store — try again");
        return;
      }
    }
    if (known.isFollowing === next) {
      writeKnownStore(queryClient, known);
      return;
    }

    settlesLater = true;
    deps.follow(
      { slug: store.slug, following: next },
      {
        onError: (error) => {
          if (!(error instanceof ApiError)) return;
          if (error.status === 401) deps.join();
          if (error.code === "requirements_missing") deps.claim();
        },
        onSettled: release,
      },
    );
  } finally {
    if (!settlesLater) release();
  }
}
